import Tooltip from "@kea-mod/common/tooltip";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import FeedbackDataService from "../../../axios/services/feedback.service";
import { ValuationField } from "./ValuationField";

export interface PickedElement {
  id: string;
  label: string;
  elementType?: string;
  eventType?: string;
}

const typeOf = (element: PickedElement, t: (key: string) => string): string | undefined => {
  if (element.elementType === "kea.BPMNEvent") {
    const byEvent: { [event: string]: string } = {
      START: "elementTypeEventStart",
      INTERMEDIATE: "elementTypeEventIntermediate",
      END: "elementTypeEventEnd",
    };
    return t(byEvent[element.eventType ?? ""] ?? "elementTypeEvent");
  }
  const byType: { [type: string]: string } = {
    "kea.BPMNActivity": "elementTypeActivity",
    "kea.BPMNGateway": "elementTypeGateway",
    "kea.BPMNSwimlane": "elementTypeSwimlane",
    "kea.Link": "elementTypeLink",
  };
  const key = byType[element.elementType ?? ""];
  return key ? t(key) : undefined;
};

// The type is always named, so the anchor reads the same way for every element. Not every
// element carries a name: in the submissions of exercise 596 only activities do, events are
// labelled through the edges next to them.
export const describePickedElement = (element: PickedElement, t: (key: string) => string): string => {
  const type = typeOf(element, t);
  const name = element.label?.trim();
  if (type && name) return `${type}: ${name}`;
  if (type) return type;
  return name || t("feedbackAnchorUnnamed");
};

type Props = {
  pickedElement: PickedElement | null;
  clearPickedElement: () => void;
  onFeedbackSaved: () => void;
  feedbacklist: Array<Object>;
  toggleModal: Function;
  setFeedbacklist: Function;
  asid: number;
  refreshManFeedback: Function;
  setManFeedToRemove: Function;
  ManFeedToRemove: Array<Object>;
  sortByGrader: (a: Object, b: Object) => number;
};

export const FeedbackCreation = (props: Props) => {
  const { t } = useTranslation();

  const [newFeedback, setNewFeedback] = useState({
    id: -1,
    content: "",
    category: "",
    valuation: "NEGATIVE",
  });
  const onChangeContent = (event: any) => {
    let value = event.target.value;

    setNewFeedback({
      ...newFeedback,
      content: value,
    });
  };
  const onChangeCategory = (event: any) => {
    let value = event.target.value;

    setNewFeedback({
      ...newFeedback,
      category: value,
    });
  };
  const saveFeedback = () => {
    let b = props.feedbacklist;
    let c = props.ManFeedToRemove;
    let data = {
      content: newFeedback.content,
      category: newFeedback.category,
      valuation: newFeedback.valuation,
      assessmentId: props.asid,
      positionId: props.pickedElement?.id ?? "",
    };
    FeedbackDataService.createManual(data)
      .then((response) => {
        //console.log(response);
        let resp = response.data.data;
        resp["grader"] = "Manual Feedback";
        b.push(resp);
        c.push(resp);
        b.sort(props.sortByGrader);
        props.setFeedbacklist(b);
        props.setManFeedToRemove(c);
        setNewFeedback({
          id: -1,
          content: "",
          category: "",
          valuation: "NEGATIVE",
        });
        props.refreshManFeedback();
        props.onFeedbackSaved();
        props.toggleModal();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const onChangeValuation = (event: any) => {
    let value = event.target.value;
    //console.log(value);

    setNewFeedback({
      ...newFeedback,
      valuation: value,
    });
  };

  return (
    <div>
      <div>
        <div className="field">
          <label className="label" htmlFor="content">
            {t("content")}
            <Tooltip
              icon="circle-info"
              class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-right"
              content={t("contentTooltip")}
            ></Tooltip>
          </label>
          <textarea
            className="textarea"
            rows={4}
            id="content"
            name="content"
            value={newFeedback.content}
            onChange={onChangeContent}
          />
        </div>
      </div>
      <div>
        <div className="field">
          <label className="label" htmlFor="category">
            {t("category")}
            <Tooltip
              icon="circle-info"
              class="icon tooltip no-border is-white   has-tooltip-multiline has-tooltip-top"
              content={t("categoryTooltip")}
            ></Tooltip>
          </label>
          <input
            type="text"
            className="input"
            id="category"
            name="category"
            value={newFeedback.category}
            onChange={onChangeCategory}
          />
        </div>
        <ValuationField activeFeedback={newFeedback} h={onChangeValuation} />

        <div className="field">
          <label className="label">{t("feedbackAnchor")}</label>
          {props.pickedElement ? (
            <div className="is-flex is-align-items-center" style={{ gap: "0.75rem" }}>
              <span className="tag is-info is-light">{describePickedElement(props.pickedElement, t)}</span>
              <button className="button is-small is-text" onClick={() => props.clearPickedElement()}>
                {t("feedbackAnchorClear")}
              </button>
            </div>
          ) : (
            <p className="help">{t("feedbackAnchorHintDialog")}</p>
          )}
        </div>

        <button className="button is-success" onClick={() => saveFeedback()}>
          {t("save")}
        </button>
      </div>
    </div>
  );
};
